//src/components/main/CoachSchedulesAdmin.js
import React, { useState, useEffect } from 'react';
import {
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  makeStyles
} from '@material-ui/core';
import moment from 'moment';
import {
  fetchCoachSchedules,
  createCoachSchedule,
  updateCoachSchedule,
  deleteCoachSchedule
} from '../apicalls/schedules';
import { getAllLocations } from '../apicalls/locations';
import { isAuthenticated } from '../apicalls/auth';
import Paginations from './Paginations';

const useStyles = makeStyles(() => ({
  container: { padding: '1rem' },
  coachHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' },
  profilePic: { width: 40, height: 40, borderRadius: '50%', marginRight: 12 },
  actions: { display: 'flex', gap: '0.5rem' },
  formField: { marginBottom: '1rem' },
  addBtn: { marginBottom: '1rem' },
  details: { marginBottom: '1rem', border: '1px solid #ccc', borderRadius: 4, padding: '0.5rem' },
  summary: { cursor: 'pointer', outline: 'none' }
}));

const DAYS = ['MONDAY','TUESDAY','WEDNESDAY','THURSDAY','FRIDAY','SATURDAY','SUNDAY'];

export default function CoachSchedulesAdmin() {
  const classes = useStyles();
  const [coaches, setCoaches] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [currentCoach, setCurrentCoach] = useState(null);
  const [form, setForm] = useState({ user_id:'', location_id:'', day:'', court:'', from:'09:00', to:'17:00' });
  const [searchTerm, setSearchTerm] = useState('');
  const [locations, setLocations] = useState([]);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const filteredCoaches = coaches.filter(coach =>
    coach.coach_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    coach.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Correct total for pagination based on the filtered results
  const totalFiltered = filteredCoaches.length;
  const startIdx = (currentPage - 1) * perPage;
  const currentCoaches = filteredCoaches.slice(startIdx, startIdx + perPage);

  // Fetch data only once when the component mounts
  useEffect(() => {
    load();
    const user = isAuthenticated();
    if (user?.access_token) {
      getAllLocations(user.access_token, 1000, 1, '')
        .then(res => setLocations(res || []));
    }
  }, []);

  function load() {
    // Fetch all coaches. The API returns the full list, so pagination params might be ignored.
    // We pass a large number for `perPage` just in case, to ensure we get all records.
    fetchCoachSchedules(1000, 1).then(response => {
      // The API returns a direct array, so we check for that first.
      if (Array.isArray(response)) {
        setCoaches(response);
      } else if (response && Array.isArray(response.data)) {
        // Fallback for the originally expected Laravel-style paginated response.
        setCoaches(response.data);
      } else {
        // Handle any other unexpected shape.
        setCoaches([]);
      }
    });
  }

  function openForm(schedule = null, coach = null) {
    if (schedule && coach) {
      setEditingId(schedule.id);
      setCurrentCoach(coach.user_id);
      setForm({
        user_id: coach.user_id,
        location_id: schedule.location_id,
        day: schedule.day,
        court: schedule.court || '',
        from: schedule.from.slice(0,5),
        to: schedule.to.slice(0,5)
      });
    } else if (coach) {
      setEditingId(null);
      setCurrentCoach(coach.user_id);
      setForm({ user_id: coach.user_id, location_id:'', day:'', court:'', from:'09:00', to:'17:00' });
    }
    setOpen(true);
  }

  function handleChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  }

  function save() {
    const action = editingId
      ? updateCoachSchedule(editingId, form)
      : createCoachSchedule(form);
    action.then(() => { load(); setOpen(false); });
  }

  function remove(id) {
    if (!window.confirm('Really delete this schedule?')) return;
    deleteCoachSchedule(id).then(load);
  }

  return (
    <div className="makeStyles-container-1 container py-4">
      <TextField
        label="Search coaches"
        variant="outlined"
        size="small"
        fullWidth
        value={searchTerm}
        onChange={e => {
          setSearchTerm(e.target.value);
          setCurrentPage(1); // Reset to first page on new search
        }}
        style={{ marginBottom: '1rem' }}
      />
      <Select
        value={perPage}
        onChange={e => { setPerPage(e.target.value); setCurrentPage(1); }}
        variant="outlined"
        size="small"
        style={{ marginBottom: '1rem', minWidth: 120 }}
      >
        {[5, 10, 20, 50, 100].map(n => (
          <MenuItem key={n} value={n}>{n} per page</MenuItem>
        ))}
      </Select>
      {currentCoaches.length > 0 ? (
        currentCoaches.map(coach => (
          <div key={coach.user_id} className={classes.details}>
            <div className={classes.summary}>
              <div className={classes.coachHeader}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <img src={coach.profile_picture} alt="" className={classes.profilePic} />
                  <div>
                    <Typography variant="subtitle1">{coach.coach_name}</Typography>
                    <Typography variant="body2" color="textSecondary">{coach.email}</Typography>
                  </div>
                </div>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  className={classes.addBtn}
                  onClick={() => openForm(null, coach)}
                >
                  + Add Schedule
                </Button>
              </div>
            </div>

            {(coach.schedules || []).length > 0 ? (
              <TableContainer component={Paper} style={{ width: '100%' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Day</TableCell>
                      <TableCell>From</TableCell>
                      <TableCell>To</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Court</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {(coach.schedules || []).map(sch => (
                      <TableRow key={sch.id}>
                        <TableCell>{sch.day}</TableCell>
                        <TableCell>{moment(sch.from, 'HH:mm:ss').format('HH:mm')}</TableCell>
                        <TableCell>{moment(sch.to, 'HH:mm:ss').format('HH:mm')}</TableCell>
                        <TableCell>{sch.location_name}</TableCell>
                        <TableCell>{sch.court ?? '–'}</TableCell>
                        <TableCell className={classes.actions}>
                          <Button size="small" onClick={() => openForm(sch, coach)}>Edit</Button>
                          <Button size="small" color="secondary" onClick={() => remove(sch.id)}>Delete</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" style={{ marginTop: 8 }}>No schedules yet.</Typography>
            )}
          </div>
        ))
      ) : (
        <Typography variant="body1">No coaches found.</Typography>
      )}

      {/* Pagination controls */}
      {/* Use the total count of the *filtered* coaches for pagination */}
      {totalFiltered > perPage && (
        <Paginations
          perPage={perPage}
          total={totalFiltered}
          paginate={page => setCurrentPage(page)}
          currentPage={currentPage}
        />
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editingId ? 'Edit' : 'New'} Schedule</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" gutterBottom>Coach ID: {currentCoach}</Typography>
          <Select
            name="location_id" value={form.location_id} onChange={handleChange}
            displayEmpty fullWidth className={classes.formField}
          >
            <MenuItem value="" disabled>Select location</MenuItem>
            {locations.map(loc => (
              <MenuItem key={loc.id} value={loc.id}>{loc.location}</MenuItem>
            ))}
          </Select>
          <Select
            name="day" value={form.day} onChange={handleChange}
            displayEmpty fullWidth className={classes.formField}
          >
            <MenuItem value="" disabled>Select day</MenuItem>
            {DAYS.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
          </Select>
          <TextField
            name="court" label="Court #"
            value={form.court} onChange={handleChange}
            fullWidth className={classes.formField}
          />
          <TextField
            name="from" label="From" type="time"
            value={form.from} onChange={handleChange}
            fullWidth className={classes.formField} InputLabelProps={{ shrink:true }}
          />
          <TextField
            name="to" label="To" type="time"
            value={form.to} onChange={handleChange}
            fullWidth className={classes.formField} InputLabelProps={{ shrink:true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button color="primary" onClick={save}>Save</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}